import { FC, useMemo, useRef, useState } from 'react';

import {
  DashboardRole,
  // ── [개발 전용] 아래 컨트롤러들은 대시보드 API 콘솔 덤프에만 쓰였다.
  // 대시보드 개발 완료(2026-07-13)로 덤프 useEffect와 함께 주석 처리.
  // 재활성화 시 이 import와 아래 useEffect 블록, react의 useEffect를 함께 되살리면 된다.
  // getCouponWindfallAnalysis,
  // getDashboardProfitAnalysis,
  // getDashboardProfitAnalysisDetail,
  // getDashboardSalesDetail,
  // getDashboardSummary,
  // getDeliveryWindfallAnalysis,
} from '@shared/api/giftOrder/giftOrder.controller';

import { DashboardProfitTab } from '../components/DashboardProfitTab';
import { DashboardSalesDetailTab } from '../components/DashboardSalesDetailTab';
import { DashboardSummaryTab } from '../components/DashboardSummaryTab';
import { DashboardWindfallTab } from '../components/DashboardWindfallTab';
import {
  endOfMonth,
  fmtMonth,
  fmtMonthKo,
  parseMonth,
  startOfMonth,
} from '../utils/dashboard.utils';

type DashboardTab = 'summary' | 'profit' | 'windfall' | 'sales';

const TABS: { key: DashboardTab; label: string }[] = [
  { key: 'summary', label: '요약' },
  { key: 'profit', label: '수익 분석' },
  { key: 'sales', label: '매출 상세' },
  { key: 'windfall', label: '낙전 분석' },
];

export const GiftOrderDashboardPage: FC<{ role?: DashboardRole }> =
  function GiftOrderDashboardPage({ role }) {
    const now = new Date();
    const [month, setMonth] = useState(fmtMonth(now));
    const [tab, setTab] = useState<DashboardTab>('summary');
    const monthInputRef = useRef<HTMLInputElement>(null);

    const { from, to } = useMemo(() => {
      const d = parseMonth(month);
      return { from: startOfMonth(d).toISOString(), to: endOfMonth(d).toISOString() };
    }, [month]);

    // ── [개발 전용] 대시보드 API 6종 응답을 한 번에 콘솔로 덤프 ──────────────
    // 대시보드 개발 완료(2026-07-13)로 비활성화. 다시 확인이 필요하면 아래 블록과
    // 상단의 컨트롤러 import, react의 useEffect import를 함께 되살리면 된다.
    // 응답 인터셉터(handleAxiosDates)가 ISO 문자열을 Date로 바꿔 놓으므로,
    // 콘솔에 보이는 날짜 필드는 문자열이 아니라 Date 객체다.
    /*
    useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      let disposed = false;
      const calls = [
        ['summary', () => getDashboardSummary(from, to, role)],
        ['sales-detail', () => getDashboardSalesDetail(from, to, role)],
        ['profit-analysis', () => getDashboardProfitAnalysis(from, to, role)],
        [
          'profit-analysis-detail',
          () => getDashboardProfitAnalysisDetail(from, to, role),
        ],
        ['windfall/coupon', () => getCouponWindfallAnalysis(from, to, role)],
        ['windfall/delivery', () => getDeliveryWindfallAnalysis(from, to, role)],
      ] as const;

      (async () => {
        const results = await Promise.all(
          calls.map(async ([name, run]) => {
            try {
              return [name, await run()] as const;
            } catch (e) {
              return [name, { __error: e }] as const;
            }
          }),
        );
        if (disposed) return;
        console.groupCollapsed(
          `[대시보드 API] ${from.slice(0, 10)} ~ ${to.slice(0, 10)} · role=${
            role ?? 'admin'
          }`,
        );
        for (const [name, payload] of results) console.log(name, payload);
        console.groupEnd();
      })();

      return () => {
        disposed = true;
      };
    }, [from, to, role]);
    */

    const handlePrevMonth = () => {
      const d = parseMonth(month);
      d.setMonth(d.getMonth() - 1);
      setMonth(fmtMonth(d));
    };
    const handleNextMonth = () => {
      const d = parseMonth(month);
      d.setMonth(d.getMonth() + 1);
      if (d <= now) setMonth(fmtMonth(d));
    };

    return (
      <div className="min-h-screen bg-gray-100 p-6">
        {/* 헤더 */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">수익 대시보드</h1>
            <div className="flex items-center rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
              {TABS.map((t) => (
                <div key={t.key} className="flex items-center">
                  {t.key === 'windfall' && (
                    <span className="mx-1.5 h-4 w-px bg-gray-400/60" />
                  )}
                  <button
                    onClick={() => setTab(t.key)}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      tab === t.key
                        ? t.key === 'windfall'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-white text-gray-900 shadow-sm'
                        : t.key === 'windfall'
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t.label}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab !== 'windfall' && (
              <>
                <button
                  onClick={handlePrevMonth}
                  className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50"
                >
                  ◀
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => monthInputRef.current?.showPicker()}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                  >
                    {fmtMonthKo(parseMonth(month))}
                  </button>
                  <input
                    ref={monthInputRef}
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    max={fmtMonth(now)}
                    className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                    tabIndex={-1}
                  />
                </div>
                <button
                  onClick={handleNextMonth}
                  disabled={fmtMonth(parseMonth(month)) >= fmtMonth(now)}
                  className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ▶
                </button>
              </>
            )}
          </div>
        </div>

        {tab === 'summary' && <DashboardSummaryTab from={from} to={to} role={role} />}
        {tab === 'profit' && <DashboardProfitTab from={from} to={to} role={role} />}
        {tab === 'windfall' && <DashboardWindfallTab from={from} to={to} role={role} />}
        {tab === 'sales' && <DashboardSalesDetailTab from={from} to={to} role={role} />}
      </div>
    );
  };

export default GiftOrderDashboardPage;
